import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Axios from 'axios';

// Import sub-components
import Login from './Login';
import NewInvoice from './NewInvoice';
import EditInvoice from './InvoiceEdit';  
import History from './History';
import Reports from './Reports';
import MonthlyReports from './MonthlyReports';
import YearlyReports from './YearlyReports';
import ResetPassword from './ResetPassword';
import defaultLogo from './logo.png';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem("token"));
  const [view, setView] = useState('dashboard'); 
  const [sharedFormData, setSharedFormData] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    setIsAuthenticated(false);
  };

  /**
   * BRIDGE: LOAD HISTORY INTO EDITOR
   */
  const loadInvoiceFromHistory = async (data) => {
    try {
      const [itemsRes, transRes] = await Promise.all([
        Axios.get(`http://localhost:3001/api/invoice-items/${data.id}`),
        Axios.get(`http://localhost:3001/api/invoice-payments/${data.id}`).catch(() => ({ data: [] }))
      ]);

      // FIX: DATE HANDLING
      // We slice the string directly rather than using new Date() to avoid timezone shifting.
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        let date = new Date(dateStr);
        // Add 1 day
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
      };

      const loadedData = {
        id: data.id,
        invoiceNo: data.invoice_no,
        clientName: data.client_name,
        company: data.company_name,
        address: data.address,
        // --- Dates Fixed Here ---
        issueDate: formatDate(data.issue_date),
        dueDate: formatDate(data.due_date),
        // -------------------------
        directorName: data.director_name || "",
        directorTitle: data.director_title || "",
        signature: data.signature_image || "",
        items: itemsRes.data.map(item => ({
          desc: item.description,
          rate: Number(item.rate),
          qty: item.qty
        })),
        transactions: transRes.data.map(t => ({
          date: formatDate(t.payment_date),
          method: t.method || t.payment_method,
          amount: Number(t.amount)
        })),
        membershipType: data.membership_type,
        discount: Number(data.discount || 0),
        vatRate: Number(data.tax_rate || 15), // Maps SQL tax_rate to React vatRate
        currency: data.currency || "BDT",
        currencySymbol: data.currency_symbol || "TK",
        status: data.status
      };

      setSharedFormData(loadedData);
      setView('editinvoice'); 
    } catch (err) {
      console.error("Error loading invoice items:", err);
      alert("Could not load full invoice details.");
    }
  };

  return (
    <Router>
      <div className="app-main-wrapper" style={{ backgroundColor: '#010314', minHeight: '100vh' }}>
        <Routes>
          <Route 
            path="/" 
            element={!isAuthenticated ? <Login setAuth={setIsAuthenticated} /> : <Navigate to="/dashboard" />} 
          />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <div>
                  {/* NAVIGATION BAR */}
                  <nav style={navBarStyle}>
                    <div style={{ display: 'flex', gap: '18px', alignItems: 'center' }}>
                      <h2 
                        onClick={() => setView('dashboard')} 
                        style={{ margin: '0 60px 0 0', fontSize: '1.2rem', cursor: 'pointer' }}
                      >
                        <img src={defaultLogo} alt="Logo" style={{ maxHeight: '50px' }} />
                      </h2>
                      <button 
                        onClick={() => setView('dashboard')} 
                        style={view === 'dashboard' ? activeNavBtn : navBtnStyle}
                      >
                        Dashboard
                      </button>
                      <button 
                        onClick={() => { setSharedFormData(null); setView('editor'); }} 
                        style={view === 'editor' && !sharedFormData ? activeNavBtn : navBtnStyle}
                      >
                        New Invoice
                      </button>
                      <button 
                        onClick={() => setView('history')} 
                        style={view === 'history' ? activeNavBtn : navBtnStyle}
                      >
                        History
                      </button>
                      <button 
                        onClick={() => setView('reports')} 
                        style={view === 'reports' ? activeNavBtn : navBtnStyle}
                      >
                        Individual Reports
                      </button>
                      <button 
                        onClick={() => setView('monthlyreports')} 
                        style={view === 'monthlyreports' ? activeNavBtn : navBtnStyle}
                      >
                        Monthly Reports
                      </button>
                      <button 
                        onClick={() => setView('yearlyreports')} 
                        style={view === 'yearlyreports' ? activeNavBtn : navBtnStyle}
                      >
                        Yearly Reports
                      </button>
                    </div>
                    <button onClick={handleLogout} style={logoutBtnStyle}>Logout</button>
                  </nav>

                  {/* VIEW PANEL */}
                  <div style={{ padding: '20px' }}>
                    {view === 'dashboard' && (
                      <WelcomeView 
                        onCreate={() => { setSharedFormData(null); setView('editor'); }}
                        onHistory={() => setView('history')}
                        onReports={() => setView('reports')}
                        onMonthlyReports={() => setView('monthlyreports')}
                      />
                    )}

                    {view === 'editor' && (
                      <NewInvoice />
                    )}

                    {view === 'editinvoice' && (
                      <EditInvoice 
                        initialData={sharedFormData} 
                        clearSharedData={() => setSharedFormData(null)} 
                      />
                    )}

                    {view === 'history' && (
                      <History onLoadInvoice={loadInvoiceFromHistory} />
                    )}

                    {view === 'reports' && (
                      <Reports />
                    )}

                    {view === 'monthlyreports' && (
                      <MonthlyReports />
                    )}

                    {view === 'yearlyreports' && (
                      <YearlyReports />
                    )}
                  </div>
                </div>
              ) : (
                <Navigate to="/" />
              )
            } 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
    </Router>
  );
}

/**
 * WELCOME VIEW COMPONENT
 */
const WelcomeView = ({ onCreate, onHistory, onReports }) => {
  return (
    <div style={{ color: '#e9edef', padding: '40px', textAlign: 'center' }}>
      <div style={{ marginBottom: '60px' }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: '600', color: '#ff2d2d' }}>Welcome to BAYTASOFT CRM</h1>
        <p style={{ color: '#63889f', fontSize: '1.1rem' }}>Efficient Invoice Management & Financial Reporting</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px', maxWidth: '1000px', margin: '0 auto' }}>
        <div style={cardStyle} onClick={onCreate}>
          <div style={iconBox('#02e85a')}>+</div>
          <h3>Create Invoice</h3>
          <p style={{ color: '#8696a0' }}>Generate professional PDF invoices with real-time preview.</p>
        </div>

        <div style={cardStyle} onClick={onHistory}>
          <div style={iconBox('#00a2ff')}>🕒</div>
          <h3>History</h3>
          <p style={{ color: '#8696a0' }}>View, edit, and track payment status of all past invoices.</p>
        </div>

        <div style={cardStyle} onClick={onReports}>
          <div style={iconBox('#ffa703')}>📊</div>
          <h3>Reports</h3>
          <p style={{ color: '#8696a0' }}>Analyze revenue, profit/loss, and overall business health.</p>
        </div>
      </div>
    </div>
  );
};

// --- STYLES ---
const navBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 40px',
  backgroundColor: '#000715',
  color: 'white',
  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
  borderBottom: '1px solid #2a3942'
};

const navBtnStyle = {
  padding: '8px 16px',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  color: '#000000',
  backgroundColor: '#ffffff',
  borderRadius: '4px',
  transition: '0.2s',
  fontSize: '14px'
};

const activeNavBtn = {
  ...navBtnStyle,
  color: '#ffffff',
  backgroundColor: '#12a7fd',
  fontWeight: 'bold'
};

const logoutBtnStyle = {
  padding: '8px 16px',
  backgroundColor: 'transparent',
  color: '#c50f24',
  border: '1px solid #c50f24',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '13px'
};

const cardStyle = {
  backgroundColor: '#011433',
  padding: '30px',
  borderRadius: '12px',
  border: '1px solid #2a3942',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  textAlign: 'center'
};

const iconBox = (color) => ({
  width: '60px',
  height: '60px',
  backgroundColor: color,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  margin: '0 auto 20px auto',
  color: 'white'
});

export default App;